
import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import { Contact, ContactsUiClient, IContact } from '../../backend/backendapi';
import { handleError, initClient } from '../../../api';
import CenterOnParent from '../../util/CenterOnParent';
import Spinner from '../../util/Spinner';
import { VueGoodTable } from 'vue-good-table';
import 'vue-good-table/dist/vue-good-table.css';
import { paginationOptions, searchOptions } from '../../../util/vue-good-table';
import ContactEditModal from './ContactEditModal';
import ContactUploadModal from './ContactUploadModal';
import { emptyUUID } from '../../../util';
import ContactStore from '../../../stores/ContactStore';
import Toasted from 'vue-toasted';

Vue.use(Toasted, { duration: 2000 });

@Component({
  components: {
    ContactEditModal,
    CenterOnParent,
    Spinner,
    VueGoodTable,
    ContactUploadModal,
  },
})
export default class Contacts extends Vue {
  readonly api = initClient(ContactsUiClient);
  readonly store = new ContactStore();

  get availableSalutations(): string[] {
    return [...new Set(this.store.contacts.map(e => e.salutation).filter(e => !!e).map(e => e!))];
  }

  get columnsConfig(): any[] {
    return [
      {
        label: this.$t('common.contacts.model.firstName').toString(),
        field: 'firstName',
      },
      {
        label: this.$t('common.contacts.model.lastName').toString(),
        field: 'lastName',
      },
      {
        label: this.$t('common.contacts.model.salutation').toString(),
        field: 'salutation',
      },
      {
        label: this.$t('common.contacts.model.company').toString(),
        field: 'company',
      },
      {
        label: this.$t('common.contacts.model.email').toString(),
        field: 'email',
      },
      {
        label: this.$t('common.contacts.model.tel').toString(),
        field: 'tel',
      },
      {
        label: this.$t('general.actions').toString(),
        field: 'actions',
      },
    ];
  }

  get paginationOptions(): any {
    return paginationOptions(this.$i18n);
  }

  get searchOptions(): any {
    return searchOptions(this.$i18n);
  }

  mounted(): void {
    this.store.reload();
  }

  openUploadModal(): void {
    (this.$refs.importModal as ContactUploadModal).open();
  }

  onUploaded(): void {
    this.$toasted.info(this.$t('txtSaved').toString());
    (this.$refs.importModal as ContactUploadModal).dismiss();
    this.store.reload();
  }

  async onContactRemove(contact: Contact): Promise<void> {
    const index = this.store.contacts.findIndex(c => c.id === contact.id);
    if (index === -1) {
      return;
    }

    //remove
    try {
      await this.api.delete(this.store.contacts[index].id);
      this.store.contacts.splice(index, 1); //TODO may remove wrong element
    } catch (error) {
      handleError(this.$toasted, this.$i18n, error);
    }
  }

  onEditContact(contact: Contact): void {
    const original = this.store.contacts.find(c => c.id === contact.id);
    if (!original) {
      return;
    }

    (this.$refs.editModal as ContactEditModal<Contact>).open(original);
  }

  onNewContact(): void {
    (this.$refs.editModal as ContactEditModal<Contact>).open(Contact.fromJS({
      id: emptyUUID(),
      createdAt: new Date(),
      createdBy: emptyUUID(),
      updatedAt: new Date(),
      updatedBy: emptyUUID(),
      firstName: '',
      lastName: '',
      salutation: '',
      company: '',
      email: '',
      tel: '',
    } as IContact));
  }

  async onSave(contact: Contact): Promise<void> {
    try {
      let c: Contact;
      if (!contact.email?.trim().length) {
        contact.email = undefined; //set to undefined explicitly so backend validation works
      }
      if (!contact.id || contact.id === emptyUUID()) {
        c = await this.api.create(contact);
      } else {
        c = await this.api.update(contact.id, contact);
      }
      this.store.rememberContact(c);
      this.$toasted.info(this.$t('txtSaved').toString());
      (this.$refs.editModal as ContactEditModal<Contact>).dismiss();
    } catch (e) {
      handleError(this.$toasted, this.$i18n, e);
    }
  }
}
